import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, FileEdit, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { invalidatePageContent } from '@/hooks/useEditableContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Catalogue of all editable text blocks across the public pages.
 * Each entry: { key, label, default, multiline?, page }
 * The `default` value mirrors the current hardcoded text in the page so the
 * admin sees the starting point and edits in-place.
 */
const CATALOG = [
  // ----- Home Page -----
  { page: 'Accueil', key: 'home.hero.eyebrow', label: 'Sous-titre du hero', default: 'CRESA ASBL — RD CONGO' },
  { page: 'Accueil', key: 'home.hero.title', label: 'Titre principal', default: 'Îlots de Développement Intégral Auto-gérés en RD Congo' },
  { page: 'Accueil', key: 'home.hero.subtitle', label: 'Description du hero', multiline: true, default: "Un nouveau paradigme de développement, ancré dans les communautés, validé par 5 ans d'expérience pilote à Bushumba (Sud-Kivu)." },

  // ----- About Page -----
  { page: 'À propos', key: 'about.intro', label: 'Introduction', multiline: true, default: 'Carrefour de Réflexion Santé — une organisation de droit congolais œuvrant pour le développement intégral auto-géré en RD Congo.' },
  { page: 'À propos', key: 'about.presentation.body', label: 'Présentation — paragraphe', multiline: true, default: 'Le Carrefour de Réflexion Santé (CRESA asbl), aussi appelé « Baraza la Afia » en swahili, est une organisation sans but lucratif de droit congolais créée en 2010.' },
  { page: 'À propos', key: 'about.mission.text', label: 'Mission', multiline: true, default: "CRESA est une organisation de haute réflexion, d'accompagnement et de capacitation technique pour les organisations locales et nationales, en vue de mobilisations de masse autour des idées-forces de changement et de transformation sociale." },
  { page: 'À propos', key: 'about.vision.text', label: 'Vision', multiline: true, default: "Faire émerger un nouveau paradigme de développement en RDC fondé sur les Îlots de Développement Intégral auto-gérés, dans lesquels les communautés prennent en main leur propre avenir économique, sanitaire et écologique." },
  { page: 'À propos', key: 'about.team.body', label: 'Équipe — paragraphe', multiline: true, default: "L'équipe CRESA est composée de membres permanents, de bénévoles, de chercheurs et de partenaires locaux mobilisés autour des îlots de développement intégral." },
  { page: 'À propos', key: 'about.zones.intro', label: "Zones d'intervention — intro", multiline: true, default: "CRESA dispose actuellement de 4 provinces pilotes où elle déploie progressivement son modèle d'îlot de développement intégral :" },

  // ----- Contact Page -----
  { page: 'Contact', key: 'contact.intro', label: 'Texte d\'introduction', multiline: true, default: "Une question, un partenariat, une opportunité d'appui ? Écrivez-nous." },

  // ----- Donations Page -----
  { page: 'Faire un don', key: 'donations.intro.title', label: 'Titre intro', default: 'Soutenez Notre Mission' },
  { page: 'Faire un don', key: 'donations.intro.body', label: 'Description', multiline: true, default: 'Votre contribution aide à propulser les îlots de développement intégral auto-gérés en RD Congo.' },

  // ----- Documents Page -----
  { page: 'Documents', key: 'documents.intro', label: 'Texte d\'introduction', multiline: true, default: "Consultez en ligne nos rapports d'activité, études, brochures et autres documents publics retraçant notre démarche d'îlots de développement intégral." },

  // ----- Activities -----
  { page: 'Activités', key: 'activities.intro', label: "Texte d'introduction", multiline: true, default: 'Découvrez les actions concrètes menées par CRESA sur le terrain.' },

  // ----- Projects -----
  { page: 'Projets', key: 'projects.intro', label: "Texte d'introduction", multiline: true, default: 'Découvrez nos projets de développement communautaire dans les quatre provinces pilotes : Sud-Kivu, Haut-Katanga, Kinshasa et Kongo Central.' },

  // ----- News / Témoignages -----
  { page: 'Actualités', key: 'news.intro', label: "Texte d'introduction", multiline: true, default: "Suivez l'actualité du secteur — santé, environnement, agriculture, société et développement communautaire en RD Congo." },
  { page: 'Témoignages', key: 'testimonials.intro', label: "Texte d'introduction", multiline: true, default: 'Les paroles authentiques des bénéficiaires, partenaires et acteurs de terrain qui font vivre les îlots de développement intégral.' },

  // ----- Email Request -----
  { page: 'Demande e-mail', key: 'emailrequest.intro', label: "Texte d'introduction", multiline: true, default: "Membres de l'équipe et collaborateurs réguliers : remplissez ce formulaire pour obtenir une adresse @cresa-asbl.org. Votre demande sera examinée par l'administration." },
];

export default function PageContentManager() {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({ 'Accueil': true });

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/page-content`);
      const map = {};
      (res.data || []).forEach((c) => { map[c.key] = c.value; });
      setOverrides(map);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des contenus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (entry) => {
    const value = drafts[entry.key] ?? overrides[entry.key] ?? entry.default;
    setSaving((s) => ({ ...s, [entry.key]: true }));
    try {
      await axios.put(`${API}/admin/page-content/${entry.key}`, { value }, { withCredentials: true });
      setOverrides((o) => ({ ...o, [entry.key]: value }));
      setDrafts((d) => { const c = { ...d }; delete c[entry.key]; return c; });
      invalidatePageContent({ [entry.key]: value });
      toast.success(`« ${entry.label} » mis à jour`);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving((s) => ({ ...s, [entry.key]: false }));
    }
  };

  const handleReset = async (entry) => {
    if (!window.confirm(`Réinitialiser « ${entry.label} » à la valeur par défaut ?`)) return;
    try {
      await axios.delete(`${API}/admin/page-content/${entry.key}`, { withCredentials: true });
      setOverrides((o) => { const c = { ...o }; delete c[entry.key]; return c; });
      setDrafts((d) => { const c = { ...d }; delete c[entry.key]; return c; });
      invalidatePageContent({ [entry.key]: undefined });
      toast.success(`« ${entry.label} » réinitialisé`);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Already at default — no override existed
        setDrafts((d) => { const c = { ...d }; delete c[entry.key]; return c; });
        return;
      }
      console.error(e);
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  // Group by page
  const groups = {};
  CATALOG.forEach((entry) => {
    if (!groups[entry.page]) groups[entry.page] = [];
    groups[entry.page].push(entry);
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-[#4A6B48]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="page-content-manager">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Contenu des pages
        </h2>
        <p className="text-sm text-[#5C665A] mt-1">
          Modifiez les textes affichés dans les différentes pages publiques. Cliquez sur « Sauvegarder » pour publier vos changements immédiatement.
        </p>
      </div>

      <div className="space-y-3">
        {Object.entries(groups).map(([page, entries]) => {
          const isOpen = !!openGroups[page];
          const overriddenCount = entries.filter((e) => overrides[e.key] !== undefined).length;
          return (
            <section key={page} className="border-2 border-[#E5E3DB] rounded-lg bg-white overflow-hidden">
              <button type="button"
                onClick={() => setOpenGroups((g) => ({ ...g, [page]: !isOpen }))}
                data-testid={`page-group-${page.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#F9F8F6] hover:bg-[#E5E3DB] transition-colors">
                <span className="font-bold text-[#2E332D] inline-flex items-center gap-2">
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <FileEdit size={14} className="text-[#4A6B48]" />
                  {page}
                  {overriddenCount > 0 && (
                    <span className="text-[10px] bg-[#4A6B48] text-white px-2 py-0.5 rounded-full font-bold">
                      {overriddenCount} modifié{overriddenCount > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
                <span className="text-xs text-[#5C665A]">{entries.length} bloc{entries.length > 1 ? 's' : ''}</span>
              </button>
              {isOpen && (
                <div className="p-4 space-y-4">
                  {entries.map((entry) => {
                    const isOverridden = overrides[entry.key] !== undefined;
                    const currentValue = drafts[entry.key] !== undefined
                      ? drafts[entry.key]
                      : (overrides[entry.key] ?? entry.default);
                    const hasUnsavedChanges = drafts[entry.key] !== undefined && drafts[entry.key] !== (overrides[entry.key] ?? entry.default);

                    return (
                      <div key={entry.key} className="border-l-4 border-[#E5E3DB] pl-4" data-testid={`content-entry-${entry.key}`}>
                        <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                          <label className="text-sm font-bold text-[#2E332D]">
                            {entry.label}
                            {isOverridden && <span className="ml-2 text-[10px] text-[#D87D4A] uppercase tracking-wide">Modifié</span>}
                          </label>
                          <code className="text-[10px] text-[#5C665A] bg-[#F9F8F6] px-1.5 py-0.5 rounded">{entry.key}</code>
                        </div>
                        {entry.multiline ? (
                          <textarea
                            rows={3}
                            value={currentValue}
                            onChange={(e) => setDrafts((d) => ({ ...d, [entry.key]: e.target.value }))}
                            className="w-full px-3 py-2 border border-[#E5E3DB] rounded text-sm"
                            data-testid={`content-input-${entry.key}`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={currentValue}
                            onChange={(e) => setDrafts((d) => ({ ...d, [entry.key]: e.target.value }))}
                            className="w-full px-3 py-2 border border-[#E5E3DB] rounded text-sm"
                            data-testid={`content-input-${entry.key}`}
                          />
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => handleSave(entry)}
                            disabled={!hasUnsavedChanges || saving[entry.key]}
                            data-testid={`content-save-${entry.key}`}
                            className="inline-flex items-center gap-1 bg-[#4A6B48] text-white text-xs font-bold px-3 py-1.5 rounded hover:bg-[#3B5539] disabled:opacity-40">
                            {saving[entry.key] ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                            Sauvegarder
                          </button>
                          {isOverridden && (
                            <button onClick={() => handleReset(entry)}
                              data-testid={`content-reset-${entry.key}`}
                              className="inline-flex items-center gap-1 border-2 border-[#5C665A] text-[#5C665A] text-xs font-bold px-3 py-1.5 rounded hover:bg-[#5C665A] hover:text-white">
                              <RotateCcw size={12} /> Restaurer par défaut
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
